import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'wouter';
import { detectPlatform, shouldShowInstallPrompt, isStandalone } from '@/lib/deviceDetect';
import { InstallWalkthrough } from './InstallWalkthrough';

const AUTH_PAGES = [
  '/login',
  '/register',
  '/verify-email',
  '/onboarding',
  '/vip-offer',
  '/auth',
  '/forgot-password',
  '/reset-password',
];

export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [platform, setPlatform] = useState<string>('ios_safari');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [location] = useLocation();

  const isAuthPage = AUTH_PAGES.some(p => location.startsWith(p));
  const token = localStorage.getItem('papacito_token');

  useEffect(() => {
    if (isAuthPage || !token) return;

    const p = detectPlatform();
    setPlatform(p);

    const timer = setTimeout(() => {
      if (shouldShowInstallPrompt()) {
        setShow(true);
      }
    }, 3000);

    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleInstalled = () => {
      setShow(false);
      setShowWalkthrough(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [isAuthPage, token]);

  const handleDismiss = () => {
    setShow(false);
    setShowWalkthrough(false);
    localStorage.setItem('papacito_install_dismissed', String(Date.now()));
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleShowWalkthrough = () => {
    setShow(false);
    setShowWalkthrough(true);
  };

  const isAndroidNative = deferredPrompt && platform.startsWith('android');

  if (isAuthPage || !token) return null;

  if (showWalkthrough) {
    return <InstallWalkthrough onClose={handleDismiss} onDone={handleDismiss} />;
  }

  if (!show) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="install-prompt"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9998,
            background: 'linear-gradient(180deg, #1a0a2e 0%, #0d0018 100%)',
            borderRadius: '24px 24px 0 0',
            padding: '12px 24px 44px',
            border: '1px solid rgba(255,255,255,0.1)',
            borderBottom: 'none',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.15)',
              margin: '0 auto 16px',
            }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: 'linear-gradient(135deg, #e11d48, #9333ea)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  boxShadow: '0 4px 16px rgba(225,29,72,0.5)',
                  flexShrink: 0,
                }}
              >
                🔥
              </div>
              <div>
                <h3 style={{ color: '#ffffff', fontSize: 18, fontWeight: 700, margin: '0 0 2px' }}>
                  Install Papacito
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, margin: 0 }}>
                  Get the full app experience
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[
              { icon: '⚡', text: 'Instant' },
              { icon: '🔔', text: 'Alerts' },
              { icon: '📵', text: 'Offline' },
            ].map((b, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  padding: '8px 6px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 3 }}>{b.icon}</div>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 11,
                    margin: 0,
                    fontWeight: 500,
                  }}
                >
                  {b.text}
                </p>
              </div>
            ))}
          </div>

          {isAndroidNative ? (
            <div>
              <button
                onClick={handleAndroidInstall}
                style={{
                  width: '100%',
                  background: '#e11d48',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 16,
                  padding: '18px 0',
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: 8,
                  boxShadow: '0 4px 20px rgba(225,29,72,0.4)',
                }}
              >
                📲 Install — One Tap
              </button>
              <p
                style={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: 12,
                  textAlign: 'center',
                  margin: '0 0 16px',
                }}
              >
                No App Store needed. Works like a real app.
              </p>
            </div>
          ) : (
            <div>
              <button
                onClick={handleShowWalkthrough}
                style={{
                  width: '100%',
                  background: '#e11d48',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 16,
                  padding: '18px 0',
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: 8,
                  boxShadow: '0 4px 20px rgba(225,29,72,0.4)',
                }}
              >
                📲 Show Me How to Install
              </button>
              <p
                style={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: 12,
                  textAlign: 'center',
                  margin: '0 0 16px',
                }}
              >
                Takes 10 seconds. We'll walk you through it.
              </p>
            </div>
          )}

          <button
            onClick={handleDismiss}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.25)',
              fontSize: 13,
              cursor: 'pointer',
              padding: '10px 0',
              textAlign: 'center',
            }}
          >
            Maybe later
          </button>

          <p
            style={{
              color: 'rgba(255,255,255,0.2)',
              fontSize: 11,
              textAlign: 'center',
              margin: '8px 0 0',
              lineHeight: 1.6,
            }}
          >
            By continuing you agree to our{' '}
            <a href="/terms" style={{ color: '#e11d48', textDecoration: 'none' }}>
              Terms of Service
            </a>{' '}
            &{' '}
            <a href="/privacy" style={{ color: '#e11d48', textDecoration: 'none' }}>
              Privacy Policy
            </a>
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
